/**
 * components/HelpFab.tsx — Floating help button ("Ask")
 *
 * A persistent purple pill launcher that opens HelpChatClient as a floating
 * overlay panel.
 *
 * WHERE IT MOUNTS (2026-09-22): EVERYWHERE, from ONE place.
 *
 * It is rendered once in app/layout.tsx, beside <ConsentManager />, so every
 * route on the site has it without a line of its own. The ten hand-placed
 * <HelpFab label="…" /> call sites that used to live on ten pages are gone.
 *
 * The previous version of this comment listed those ten routes and ended
 * "a comment cannot enforce itself." That was true, and it is why this changed:
 * a list a human has to keep in step with the code is a defect waiting its turn.
 * The same weakness had already shipped one — two of six pages forgot to import
 * help-chat.css and the chat opened unstyled there.
 *
 * AFTER THIS CHANGE THERE IS NOTHING LEFT FOR A COMMENT TO ENFORCE. The route
 * behaviour is data, in lib/help-fab-labels.ts, and it is executable:
 *   - the label                   → DEFAULT_HELP_FAB_LABEL ("Ask", every route)
 *   - where it must not appear    → HELP_FAB_SUPPRESSED_ROUTES (/checkout)
 * Per-route wording was removed 2026-09-23 (one label everywhere; brand kit
 * v2.56 §14.5). A new page needs no edit here and no edit there. Read that
 * file, not this paragraph.
 *
 * Usage (the layout's mount — there should be no second one):
 *   <HelpFab />
 *
 * Props — all optional, and the layout passes none:
 *   - label?: string — overrides the "Ask" label. Left unset in the layout, and
 *     it should stay unset: the label is the same on every route (2026-09-23).
 *     Wording is Iris's lane; do not invent one here.
 *   - isAvailable?: boolean — optional runtime override; defaults to true.
 *     NEVER fed from a build-time env read (see HelpFabProps).
 *   - bottomOffset?: string — CSS value for bottom offset (per-page override).
 *     No page passed this before the move, and the layout does not pass it.
 *
 * Icon: icon-speech-bubble-instant.svg (Brand/BrandAssets/icons/utility-set-v1/)
 *       — speech bubble with a solid lightning bolt: "instant answer" without
 *       AI iconography (Aria, 2026-09-23). Inlined as SVG — currentColor
 *       resolves against page CSS.
 *       Per architectural rule: never <img src="*.svg"> for color-flexible icons.
 *
 * Visual spec: brand kit v2.56 §14.5 (Aria, 2026-09-23) — .help-fab purple
 *              var(--cta). Desktop (≥768px): 48px pill, 24px icon + "Ask" at
 *              16px. Phones (≤767px): 48×48 round dot, 24px icon, no label.
 *              The word "AI" never appears on or in the button.
 *
 * Behavior (2026-05-20):
 *   - Scroll gate: hidden until window.scrollY > 200px (fade-in 200ms ease).
 *     Fades out again when scrolling back above 200px. SSR-safe initial state.
 *   - Footer collision: IntersectionObserver on footer pushes button up when
 *     footer enters viewport so legal links are never obscured by the button.
 *   - Consent-banner collision (2026-09-22): the cookie banner is fixed to the
 *     viewport bottom at z-index 9600 and is 357px tall at 390px wide, so it
 *     covered the pill completely on phones. A MutationObserver +
 *     ResizeObserver pair pushes the pill above it. See the effect below.
 *   All three behaviors are global — they apply on every page where HelpFab
 *   mounts, which is why the phone collision is fixed HERE and not per page.
 *
 * Performance: SOP-WEBSITE-03 rule 12 (heavy-embed facade). The chat panel is
 *              rendered but hidden until the user taps the FAB.
 *
 * Governance: SOP-WEBSITE-02, SOP-WEBSITE-03, SOP-BRAND-01 v1.51.
 *
 * Restored: integrate/pre-launch-2026-05-18 (lost during branch integration;
 *           component existed in 8663992, dropped in merge, restored 2026-05-18).
 */
'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { DEFAULT_HELP_FAB_LABEL, isHelpFabSuppressed } from '@/lib/help-fab-labels';
// The panel's styles travel with the component that renders the panel.
//
// Until 2026-09-22 every page mounting HelpFab had to remember to
// `import '@/app/help-chat.css'` itself, and help-chat.css's own header already
// claimed this file was one of its importers. Four of the six pages remembered
// (/pro-member, /pro-member-v12, /store, /super-fingers, plus /contact for its
// inline embed); /piano-reading-level and /help did not, so opening the chat
// there rendered it unstyled. Owning the import here makes that unforgettable
// and is why the four pages added in this round do not import it themselves.
//
// Scope: this adds help-chat.css to the route CSS of the two pages that were
// missing it and changes nothing on the eight that already had it (the bundler
// dedupes a stylesheet imported twice). No selector in that file is altered.
import '@/app/help-chat.css';
// Cycle 67 perf: dynamic import so HelpChatClient + Cloudflare Turnstile (64.5KB)
// are NOT included in the initial page bundle. They load only when the panel opens.
// SOP-WEBSITE-03 rule 12 (heavy-embed facade). Expected TBT drop: 200-300ms/page.
const HelpChatClient = dynamic(
  () => import('@/app/help/_components/HelpChatClient'),
  { ssr: false }
);

interface HelpFabProps {
  /**
   * Optional wording override. Unset — which is how the layout mounts it — the
   * button shows DEFAULT_HELP_FAB_LABEL ("Ask") on every route, from
   * lib/help-fab-labels.ts. Do not pass it: one label everywhere (2026-09-23).
   */
  label?: string;
  /**
   * Optional runtime override. DEFAULTS TO TRUE ON PURPOSE.
   *
   * Do NOT reintroduce a build-time `!!process.env.ANTHROPIC_API_KEY` read in a
   * page to feed this prop. On Cloudflare Workers the secret only exists at
   * request time, so a statically prerendered page bakes `false` permanently and
   * the chat ships dead (measured 2026-09-08 on /help and /store: 3,512 views,
   * zero API requests). Availability is decided by /api/help-search at request
   * time — it answers 503 { unavailable: true } when the key is missing, and
   * HelpChatClient renders the friendly "being set up" message for that.
   */
  isAvailable?: boolean;
  bottomOffset?: string;
}

// Scroll threshold (px) before FAB appears
const SCROLL_THRESHOLD = 200;

export default function HelpFab({ label, isAvailable = true, bottomOffset }: HelpFabProps = {}) {
  // The label is the same on every route; the route decides only whether the
  // button exists at all. Both are data in lib/help-fab-labels.ts.
  const pathname = usePathname();
  const resolvedLabel = label ?? DEFAULT_HELP_FAB_LABEL;
  const suppressed = isHelpFabSuppressed(pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  // Scroll-gate: FAB is invisible until user scrolls past SCROLL_THRESHOLD
  const [scrollVisible, setScrollVisible] = useState(false);
  // Footer-collision: extra bottom offset when footer is in viewport
  const [footerOffset, setFooterOffset] = useState(0);
  // Consent-banner collision: extra bottom offset while the cookie banner is up
  const [bannerOffset, setBannerOffset] = useState(0);
  // True while the cookie banner is mounted. On phones (≤768px) the CSS hides
  // the dot entirely while this is set (app/globals.css §C50, Aria §2
  // 2026-10-07); above 768px it has no effect and the lift below still applies.
  const [consentOpen, setConsentOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // ── Scroll gate ──────────────────────────────────────────────────────────────
  // rAF-throttled passive scroll listener. SSR-safe: initial state hidden (false).
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setScrollVisible(window.scrollY > SCROLL_THRESHOLD);
    });
  }, []);

  useEffect(() => {
    // Run once on mount to set correct initial state (handles page already scrolled)
    setScrollVisible(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  // ── Footer collision avoidance ──────────────────────────────────────────────
  // IntersectionObserver on the page footer. When footer intersects the viewport,
  // compute visible height and push the FAB up so legal links are never obscured.
  // Smooth: CSS transition handles the visual movement (200ms ease).
  useEffect(() => {
    const footer = document.querySelector('footer[aria-label="Site footer"]') as HTMLElement | null;
    if (!footer) return;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const visibleHeight = entry.intersectionRect.height;
          setFooterOffset(visibleHeight > 0 ? visibleHeight + 8 : 0);
        } else {
          setFooterOffset(0);
        }
      });
    }, {
      // Fire as soon as any pixel of footer enters viewport; high resolution thresholds
      threshold: Array.from({ length: 101 }, (_, i) => i / 100),
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  // ── Consent-banner collision avoidance ─────────────────────────────────────
  // MEASURED 2026-09-22 on deployed staging (576bdc7e), /piano-reading-level at
  // 390x844: `.consent-banner` is `position: fixed; bottom: 0`, **357px tall**,
  // z-index 9600. The FAB sits at bottom 24px, is 44px tall (z-index 900), so it
  // rendered at top 776 / bottom 820 — entirely inside the banner's band and
  // entirely behind it. On a phone, for every visitor who has not yet answered
  // the cookie banner, the chat pill was invisible and untappable on all six
  // pages that mounted it. The footer observer below never saw this because the
  // banner is fixed chrome, not the footer.
  //
  // Same shape as the footer observer: measure, push the FAB above it, let the
  // CSS transition move it. The banner is a client component that mounts after
  // hydration and is removed when answered, so a plain querySelector at mount is
  // not enough — a MutationObserver watches for it arriving and leaving, and a
  // ResizeObserver tracks its height (it reflows when the toggles row wraps).
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let tracked: HTMLElement | null = null;

    const measure = (el: HTMLElement) => {
      const h = el.getBoundingClientRect().height;
      setBannerOffset(h > 0 ? h + 8 : 0);
    };

    const track = () => {
      const banner = document.querySelector('.consent-banner') as HTMLElement | null;
      if (banner === tracked) return;
      resizeObserver?.disconnect();
      tracked = banner;
      setConsentOpen(!!banner);
      if (!banner) {
        setBannerOffset(0);
        return;
      }
      measure(banner);
      resizeObserver = new ResizeObserver(() => measure(banner));
      resizeObserver.observe(banner);
    };

    // SOP-WEBSITE-03 Rule 3 (speed pass 2026-09-28): no layout read during
    // hydration — the first measure waits for the next animation frame. And
    // the observer watches only the banner's own container: ConsentManager
    // renders the banner as a direct child of <body> (app/layout.tsx), so
    // body's own child list is enough; a subtree watch fired on every DOM
    // change anywhere on the page.
    const first = requestAnimationFrame(track);
    const mutationObserver = new MutationObserver(track);
    mutationObserver.observe(document.body, { childList: true });

    return () => {
      cancelAnimationFrame(first);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  // Close panel on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        fabRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Focus panel on open
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  // Compute bottom: base (prop or CSS-var default) + whichever bottom-of-screen
  // obstruction is taller. `max` and not a sum: the consent banner is fixed to
  // the viewport bottom and paints OVER the footer, so when both are present
  // clearing the banner already clears the footer. Summing them would throw the
  // pill into the middle of the screen.
  const baseBottom = bottomOffset ?? 'var(--space-24)';
  const collisionOffset = Math.max(footerOffset, bannerOffset);
  const fabStyle: React.CSSProperties = {
    ...(bottomOffset ? ({ '--help-fab-bottom-offset': bottomOffset } as React.CSSProperties) : {}),
    // Scroll gate: opacity + pointer-events (200ms ease matches CSS transition)
    opacity: scrollVisible ? 1 : 0,
    pointerEvents: scrollVisible ? 'auto' : 'none',
    // Collision: shift button up by the obstruction's height + 8px gap
    ...(collisionOffset > 0 ? { bottom: `calc(${baseBottom} + ${collisionOffset}px)` } : {}),
    // Extend existing CSS transition with opacity + bottom
    transition: 'opacity 200ms ease, bottom 200ms ease, background var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast)',
  };

  // Panel style: mirrors footer offset; panel positions itself relative to FAB via CSS
  const panelStyle: React.CSSProperties = {
    ...(bottomOffset ? ({ '--help-fab-bottom-offset': bottomOffset } as React.CSSProperties) : {}),
    ...(collisionOffset > 0 ? {
      bottom: `calc(${baseBottom} + 48px + var(--space-12) + ${collisionOffset}px)`,
      transition: 'bottom 200ms ease',
    } : {}),
  };

  // Suppressed routes render nothing. This sits AFTER every hook on purpose —
  // an early return above them would change the hook order between routes and
  // break the rules of hooks. /checkout is the only entry today: a floating
  // overlay must never cover a field on a payment form (SOP-WEBSITE-03 rule 19).
  if (suppressed) return null;

  return (
    <>
      {/* Floating pill button */}
      <button
        ref={fabRef}
        type="button"
        className={`help-fab${isOpen ? ' help-fab--open' : ''}`}
        aria-label="Ask a question — get an instant answer"
        title="Get an instant answer"
        aria-expanded={isOpen}
        aria-controls="help-fab-panel"
        onClick={() => setIsOpen(prev => !prev)}
        style={fabStyle}
        data-consent-open={consentOpen ? 'true' : undefined}
      >
        {/* icon-speech-bubble-instant.svg inlined for currentColor cascade */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 22 22"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          aria-hidden="true"
          className="help-fab__icon"
          width={24}
          height={24}
        >
          <path d="M5.5 2.5 C3.6 2.4 2.5 3.5 2.5 5.3 L2.6 13.0 C2.6 14.8 3.8 15.8 5.6 15.8 L4.5 15.8 C4.2 16.5 3.5 18.0 2.8 19.2 C4.5 18.5 6.5 17.2 7.5 15.8 L16.5 15.7 C18.3 15.7 19.5 14.6 19.5 12.8 L19.4 5.2 C19.4 3.4 18.2 2.4 16.4 2.4 Z"/>
          <path d="M12.9 4.0 C11.6 6.1 9.8 8.1 8.2 10.1 C9.5 10.15 10.85 10.15 12.15 10.2 C11.75 11.35 11.4 12.5 11.3 13.4 C13.3 11.2 15.1 9.15 16.9 7.0 C15.55 6.95 14.2 6.9 12.85 6.9 C13.2 5.9 13.3 4.95 12.9 4.0 Z" fill="currentColor" strokeWidth="1"/>
        </svg>
        <span className="help-fab__label">{resolvedLabel}</span>
      </button>

      {/* Floating panel — rendered after mount (avoids SSR mismatch) */}
      {hasMounted && (
        <div
          ref={panelRef}
          id="help-fab-panel"
          role="dialog"
          aria-modal="true"
          aria-label="AI help assistant"
          tabIndex={-1}
          className={`help-fab-panel${isOpen ? ' help-fab-panel--open' : ''}`}
          style={panelStyle}
          hidden={!isOpen}
        >
          <div className="help-fab-panel__header">
            <span className="help-fab-panel__title">Ask anything</span>
            <button
              type="button"
              className="help-fab-panel__close"
              aria-label="Close help assistant"
              onClick={() => {
                setIsOpen(false);
                fabRef.current?.focus();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                width={20}
                height={20}
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="help-fab-panel__body">
            {/* HelpChatClient only mounts when the panel is open — not on page load.
                Combined with the dynamic import above, this ensures Turnstile and
                the full chat UI (64.5KB) never load until the user opens the FAB. */}
            {isOpen && <HelpChatClient isAvailable={isAvailable} />}
          </div>
        </div>
      )}
    </>
  );
}
